import { brand } from "../../../../config/theme";
import { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import type { User } from "../../../../store/admin/usersStore";
import type { UserFormData } from "../components/UserForm";
import api from "../../../../utils/api";
import type { AlertState } from "../types";
import { useCompaniesStore } from "../../../../store/admin/companiesStore";
import {
  useUsersList,
  type UserListParams,
} from "../../../../features/users/queries";
import {
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from "../../../../features/users/mutations";
import { useInvalidateDomain } from "../../../../lib/invalidation";
import { useIsUserDev } from "../../../../hooks/useIsUserDev";
import { SEARCH_DEBOUNCE_MS } from "../../../../hooks/useDebounce";
import { escapeHtml } from "../../../../utils/escapeHtml";

// Role ID del admin_reseller
const ADMIN_RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ";

// Role ID del driver
const DRIVER_ROLE_ID = "vRXfmHHgR5ygbKgjidpA";

// Formatear teléfono a E.164 estándar
const formatPhoneToE164 = (phone: string | undefined): string | undefined => {
  if (!phone || phone.trim() === "") return undefined;

  // Eliminar espacios, guiones, paréntesis y otros caracteres no numéricos excepto +
  let cleaned = phone.replace(/[^0-9+]/g, "");

  // Si ya tiene + al inicio, asumir que ya está en formato E.164
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // Si es un número de 10 dígitos (México), agregar +52
  if (cleaned.length === 10) {
    return `+52${cleaned}`;
  }

  // Si es un número de 12 dígitos (ya incluye 52), agregar +
  if (cleaned.length === 12 && cleaned.startsWith("52")) {
    return `+${cleaned}`;
  }

  // Para otros casos, intentar agregar + al inicio
  return cleaned.length > 0 ? `+${cleaned}` : undefined;
};

export function useUserForm() {
  // Get selected company from companies store (for reseller company selection)
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);

  const { isUserDev } = useIsUserDev();

  // Local states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit">("details");
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [userForPasswordChange, setUserForPasswordChange] = useState<User | null>(null);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Alert state
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  // Get role_id and company_id from localStorage
  const getRoleId = () => {
    return localStorage.getItem("userRoleId") || "";
  };

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // Server data via TanStack Query (paginated, company-scoped)
  const filterParams = useMemo<UserListParams>(() => {
    const params: UserListParams = { page: currentPage, limit: 9 };
    const search = debouncedSearch.trim();
    if (search !== "") params.search = search;
    if (selectedStatus && selectedStatus !== "all")
      params.status_id = selectedStatus;
    return params;
  }, [currentPage, debouncedSearch, selectedStatus]);

  const usersQuery = useUsersList(filterParams);
  const users = usersQuery.data?.data ?? [];
  const meta = usersQuery.data?.meta;
  const loading = usersQuery.isLoading;

  const invalidateDomain = useInvalidateDomain();
  const invalidateUsers = () => invalidateDomain("users");

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  // Reset filters and page when company changes
  const prevCompanyRef = useRef(selectedCompanyId);
  useEffect(() => {
    if (prevCompanyRef.current !== selectedCompanyId) {
      prevCompanyRef.current = selectedCompanyId;
      setCurrentPage(1);
      setSelectedStatus("all");
      setSearchQuery("");
      setDebouncedSearch("");
    }
  }, [selectedCompanyId]);

  // Handlers
  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleDetailsClick = (user: User) => {
    setSelectedUser(user);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = async (user: User) => {
    setSelectedUser(user);
    setIsEdit(true);
    setEditUserId(user.id);
    setShowAddModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleAddClick = () => {
    setSelectedUser(null);
    setIsEdit(false);
    setEditUserId(null);
    setShowAddModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    document.body.style.overflow = "";
  };

  const handleDeleteUser = async (user: User) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      html: `¿Deseas desactivar al usuario <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        setLoadingSubmit(true);

        // Mostrar modal de carga
        Swal.fire({
          title: "Desactivando usuario...",
          html: `Por favor espera mientras se desactiva a <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong>`,
          icon: "info",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const requestBody = {
          userID: user.id,
          user: {
            status_id: "S6uX1vY5zZ0dG7eI3bJ9",
            company_id: user.company_id, // Mantener company_id existente
          },
        };

        await updateUserMutation.mutateAsync(requestBody); // 60 segundos timeout

        // Cerrar modal de carga y mostrar éxito
        Swal.fire({
          title: "¡Desactivado!",
          html: `El usuario <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong> ha sido desactivado correctamente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } catch (error: any) {
        console.error("Error al desactivar usuario:", error);

        Swal.fire({
          title: "Error",
          html: `No se pudo desactivar el usuario. Error: ${escapeHtml(String(error.response?.data?.message || error.message || "Error desconocido"))}`,
          icon: "error",
          confirmButtonColor: brand.primary,
        });
      } finally {
        setLoadingSubmit(false);
      }
    }
  };

  const handleActivateUser = async (user: User) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      html: `¿Deseas activar al usuario <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        setLoadingSubmit(true);

        Swal.fire({
          title: "Activando usuario...",
          html: `Por favor espera mientras se activa a <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong>`,
          icon: "info",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const requestBody = {
          userID: user.id,
          user: {
            status_id: "R5tW0uX4yY9cF6dH2aI8",
            company_id: user.company_id,
          },
        };

        await updateUserMutation.mutateAsync(requestBody);

        Swal.fire({
          title: "¡Activado!",
          html: `El usuario <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong> ha sido activado correctamente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } catch (error: any) {
        console.error("Error al activar usuario:", error);

        Swal.fire({
          title: "Error",
          html: `No se pudo activar el usuario. Error: ${escapeHtml(String(error.response?.data?.message || error.message || "Error desconocido"))}`,
          icon: "error",
          confirmButtonColor: brand.primary,
        });
      } finally {
        setLoadingSubmit(false);
      }
    }
  };

  const handleChangePassword = (user: User) => {
    setUserForPasswordChange(user);
    setShowChangePasswordModal(true);
  };

  const handleConfirmPasswordChange = async (password: string) => {
    if (!userForPasswordChange) return;

    try {
      setLoadingSubmit(true);

      // Llamar al endpoint para cambiar contraseña
      await api.post("/users/change-password", {
        userId: userForPasswordChange.id,
        password: password,
      });

      Swal.fire({
        title: "¡Contraseña cambiada!",
        html: `La contraseña del usuario <strong>${escapeHtml(`${userForPasswordChange.first_name} ${userForPasswordChange.last_name}`)}</strong> ha sido cambiada correctamente.`,
        icon: "success",
        confirmButtonColor: brand.primary,
      });

      setShowChangePasswordModal(false);
      setUserForPasswordChange(null);
    } catch (error: any) {
      console.error("Error al cambiar contraseña:", error);

      Swal.fire({
        title: "Error",
        html: `No se pudo cambiar la contraseña. Error: ${escapeHtml(String(error.response?.data?.message || error.message || "Error desconocido"))}`,
        icon: "error",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowChangePasswordModal(false);
    setUserForPasswordChange(null);
  };

  const handleBulkDeactivateUsers = async (usersToDeactivate: User[]) => {
    if (usersToDeactivate.length === 0) return;
    const count = usersToDeactivate.length;

    const result = await Swal.fire({
      title: "¿Estás seguro?",
      html: `¿Deseas desactivar <strong>${count}</strong> ${count === 1 ? "usuario" : "usuarios"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setLoadingSubmit(true);

      Swal.fire({
        title: `Desactivando ${count === 1 ? "usuario" : "usuarios"}...`,
        html: `Por favor espera`,
        icon: "info",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const errors: string[] = [];
      for (const user of usersToDeactivate) {
        try {
          await api.put(
            "/user",
            {
              userID: user.id,
              user: {
                status_id: "S6uX1vY5zZ0dG7eI3bJ9",
                company_id: user.company_id,
              },
            },
            { timeout: 60000 },
          );
        } catch (error: any) {
          errors.push(
            `${escapeHtml(`${user.first_name} ${user.last_name}`)}: ${error.response?.data?.message || error.message || "Error"}`,
          );
        }
      }

      if (errors.length === 0) {
        Swal.fire({
          title: "¡Desactivados!",
          html: `${count} ${count === 1 ? "usuario ha sido desactivado" : "usuarios han sido desactivados"} correctamente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: "Proceso completado con errores",
          html: `Éxitos: ${count - errors.length} / ${count}<br><br>Errores:<br>${errors.map((e) => escapeHtml(String(e))).join("<br>")}`,
          icon: "warning",
          confirmButtonColor: brand.primary,
        });
      }

      invalidateUsers();
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleBulkActivateUsers = async (usersToActivate: User[]) => {
    if (usersToActivate.length === 0) return;
    const count = usersToActivate.length;

    const result = await Swal.fire({
      title: "¿Estás seguro?",
      html: `¿Deseas activar <strong>${count}</strong> ${count === 1 ? "usuario" : "usuarios"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setLoadingSubmit(true);

      Swal.fire({
        title: `Activando ${count === 1 ? "usuario" : "usuarios"}...`,
        html: `Por favor espera`,
        icon: "info",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const errors: string[] = [];
      for (const user of usersToActivate) {
        try {
          await api.put(
            "/user",
            {
              userID: user.id,
              user: {
                status_id: "R5tW0uX4yY9cF6dH2aI8",
                company_id: user.company_id,
              },
            },
            { timeout: 60000 },
          );
        } catch (error: any) {
          errors.push(
            `${escapeHtml(`${user.first_name} ${user.last_name}`)}: ${error.response?.data?.message || error.message || "Error"}`,
          );
        }
      }

      if (errors.length === 0) {
        Swal.fire({
          title: "¡Activados!",
          html: `${count} ${count === 1 ? "usuario ha sido activado" : "usuarios han sido activados"} correctamente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: "Proceso completado con errores",
          html: `Éxitos: ${count - errors.length} / ${count}<br><br>Errores:<br>${errors.map((e) => escapeHtml(String(e))).join("<br>")}`,
          icon: "warning",
          confirmButtonColor: brand.primary,
        });
      }

      invalidateUsers();
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleBulkHardDeleteUsers = async (usersToDelete: User[]) => {
    if (usersToDelete.length === 0) return;
    const count = usersToDelete.length;

    const result = await Swal.fire({
      title: "¿Eliminar usuarios permanentemente?",
      html: `¿Deseas eliminar <strong>${count}</strong> ${count === 1 ? "usuario" : "usuarios"}? ¡Esta acción no se puede deshacer!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setLoadingSubmit(true);

      Swal.fire({
        title: `Eliminando ${count === 1 ? "usuario" : "usuarios"}...`,
        html: `Por favor espera`,
        icon: "info",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const errors: string[] = [];
      for (const user of usersToDelete) {
        try {
          await api.delete("/user", {
            data: { userID: user.id },
            timeout: 60000,
          });
        } catch (error: any) {
          errors.push(
            `${escapeHtml(`${user.first_name} ${user.last_name}`)}: ${error.response?.data?.message || error.message || "Error"}`,
          );
        }
      }

      if (errors.length === 0) {
        Swal.fire({
          title: "¡Eliminados!",
          html: `${count} ${count === 1 ? "usuario ha sido eliminado" : "usuarios han sido eliminados"} permanentemente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: "Proceso completado con errores",
          html: `Éxitos: ${count - errors.length} / ${count}<br><br>Errores:<br>${errors.map((e) => escapeHtml(String(e))).join("<br>")}`,
          icon: "warning",
          confirmButtonColor: brand.primary,
        });
      }

      invalidateUsers();
    } finally {
      setLoadingSubmit(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditUserId(null);
    setSelectedUser(null);
    setLoadingSubmit(false);
    document.body.style.overflow = "";
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  // Verificar si el usuario logueado es admin_reseller
  const isAdminReseller = getRoleId() === ADMIN_RESELLER_ROLE_ID;

  // Eliminar usuario permanentemente (solo para admin_reseller)
  const handleHardDeleteUser = async (user: User) => {
    const result = await Swal.fire({
      title: "¿Eliminar usuario permanentemente?",
      html: `¡Esta acción no se puede deshacer!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setLoadingSubmit(true);

        // Mostrar modal de carga
        Swal.fire({
          title: "Eliminando usuario...",
          html: `Por favor espera mientras se elimina permanentemente a <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong>`,
          icon: "info",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        await deleteUserMutation.mutateAsync(user.id);

        // Cerrar modal de carga y mostrar éxito
        Swal.fire({
          title: "¡Eliminado!",
          html: `El usuario <strong>${escapeHtml(`${user.first_name} ${user.last_name}`)}</strong> ha sido eliminado permanentemente.`,
          icon: "success",
          confirmButtonColor: brand.primary,
          timer: 2000,
          timerProgressBar: true,
        });
      } catch (error: any) {
        console.error("Error al eliminar usuario:", error);

        Swal.fire({
          title: "Error",
          html: `No se pudo eliminar el usuario. Error: ${escapeHtml(String(error.response?.data?.message || error.message || "Error desconocido"))}`,
          icon: "error",
          confirmButtonColor: brand.primary,
        });
      } finally {
        setLoadingSubmit(false);
      }
    }
  };

  // Crear usuario
  const handleCreateUser = async (data: UserFormData) => {
    const isDriverRole = data.role_id === DRIVER_ROLE_ID;

    try {
      setLoadingSubmit(true);

      // Mostrar modal de carga
      Swal.fire({
        title: isDriverRole ? "Creando usuario y conductor..." : "Creando usuario...",
        html: `Por favor espera mientras se crea al usuario <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong>`,
        icon: "info",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Filter out empty emergency contacts for driver
      const validContacts = isDriverRole
        ? (data.contactsEmergency || []).filter(
            (contact) => contact.name || contact.phone || contact.email
          )
        : undefined;

      // Create user (backend will also create driver if role is driver)
      await createUserMutation.mutateAsync({
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          password: data.password,
          phone: formatPhoneToE164(data.phone),
          address:
            data.address && data.address.trim() !== ""
              ? data.address
              : undefined,
          bio: data.bio?.trim() || "",
          location: data.location?.trim() || "",
          website: data.website?.trim() || "",
          notification_prefs: data.notification_prefs,
          userType: data.userType,
          company_id: data.company_id,
          role_id: data.role_id,
          status_id: data.status_id,
          ...(!isDriverRole && data.available_devices &&
            data.available_devices.length > 0 && {
              available_devices: data.available_devices,
            }),
          // Driver-specific fields (backend will use these if role is driver)
          ...(isDriverRole && {
            licenseId: data.licenseId,
            licenseExp: data.licenseExp,
            licenceExpedition: data.licenceExpedition || null,
            contactsEmergency: validContacts,
          }),
          ...(isUserDev && { userDev: data.userDev === true }),
          ...(isUserDev && { canUseSG: data.canUseSG === true }),
      });

      // Cerrar modal de carga y mostrar éxito
      Swal.fire({
        title: isDriverRole ? "¡Usuario y conductor creados!" : "¡Usuario creado!",
        html: isDriverRole
          ? `El usuario y conductor <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong> han sido creados correctamente.`
          : `El usuario <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong> ha sido creado correctamente.`,
        icon: "success",
        confirmButtonColor: brand.primary,
        timer: 2000,
        timerProgressBar: true,
      });

      closeAddModal();
    } catch (error: any) {
      console.error("Error al crear usuario:", error);

      Swal.fire({
        title: "Error",
        html: `No se pudo crear el usuario. Error: ${escapeHtml(String(error.response?.data?.meta?.message || error.response?.data?.message || error.message || "Error desconocido"))}`,
        icon: "error",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Editar usuario
  const handleUpdateUser = async (data: UserFormData) => {
    if (!editUserId) return;

    const isDriverRole = data.role_id === DRIVER_ROLE_ID;

    try {
      setLoadingSubmit(true);

      // Mostrar modal de carga
      Swal.fire({
        title: isDriverRole ? "Actualizando usuario y conductor..." : "Actualizando usuario...",
        html: `Por favor espera mientras se actualiza al usuario <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong>`,
        icon: "info",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Filter out empty emergency contacts for driver
      const validContacts = isDriverRole
        ? (data.contactsEmergency || []).filter(
            (contact) => contact.name || contact.phone || contact.email
          )
        : undefined;

      const requestBody = {
        userID: editUserId,
        user: {
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: formatPhoneToE164(data.phone),
          address:
            data.address && data.address.trim() !== ""
              ? data.address
              : undefined,
          bio: data.bio?.trim() || "",
          location: data.location?.trim() || "",
          website: data.website?.trim() || "",
          notification_prefs: data.notification_prefs,
          userType: data.userType,
          company_id: data.company_id,
          role_id: data.role_id,
          ...(data.status_id && { status_id: data.status_id }),
          // Solo enviar password si se proporcionó uno nuevo
          ...(data.password &&
            data.password.trim() !== "" && { password: data.password }),
          // Enviar available_devices si está definido y NO es driver (puede ser array vacío para limpiar)
          ...(!isDriverRole && data.available_devices !== undefined && {
            available_devices: data.available_devices,
          }),
          // Driver-specific fields (backend will handle driver update/create)
          ...(isDriverRole && {
            licenseId: data.licenseId,
            licenseExp: data.licenseExp,
            licenceExpedition: data.licenceExpedition || null,
            contactsEmergency: validContacts,
          }),
          ...(isUserDev && { userDev: data.userDev === true }),
          ...(isUserDev && { canUseSG: data.canUseSG === true }),
        },
      };

      await updateUserMutation.mutateAsync(requestBody);

      // Cerrar modal de carga y mostrar éxito
      Swal.fire({
        title: isDriverRole ? "¡Usuario y conductor actualizados!" : "¡Usuario actualizado!",
        html: isDriverRole
          ? `El usuario y conductor <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong> han sido actualizados correctamente.`
          : `El usuario <strong>${escapeHtml(`${data.first_name} ${data.last_name}`)}</strong> ha sido actualizado correctamente.`,
        icon: "success",
        confirmButtonColor: brand.primary,
        timer: 2000,
        timerProgressBar: true,
      });

      closeAddModal();
    } catch (error: any) {
      console.error("Error al actualizar usuario:", error);

      Swal.fire({
        title: "Error",
        html: `No se pudo actualizar el usuario. Error: ${escapeHtml(String(error.response?.data?.meta?.message || error.response?.data?.message || error.message || "Error desconocido"))}`,
        icon: "error",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    // Data
    users,
    meta,
    loading,
    selectedUser,
    alert,

    // Modal states
    isModalOpen,
    modalType,
    showAddModal,
    loadingSubmit,
    isEdit,
    editUserId,

    // Filter states
    selectedStatus,
    searchQuery,
    currentPage,
    selectedView,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    closeAlert,
    handleDeleteUser,
    handleHardDeleteUser,
    handleActivateUser,
    handleChangePassword,
    handleBulkDeactivateUsers,
    handleBulkActivateUsers,
    handleBulkHardDeleteUsers,
    handleCreateUser,
    handleUpdateUser,
    handleConfirmPasswordChange,
    handleClosePasswordModal,

    // Password modal states
    showChangePasswordModal,
    userForPasswordChange,

    // Admin reseller check
    isAdminReseller,
  };
}
